import {
  Button, CircularProgress,
  createStyles,
  Dialog, DialogActions,
  DialogContent,
  Divider,
  IconButton,
  makeStyles,
  Theme,
  withStyles
} from "@material-ui/core";
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import ReactCrop, { makeAspectCrop, centerCrop, PixelCrop, Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useEffect, useRef, useState } from "react";
import {useDispatch, useSelector} from "react-redux";
import {updateUserProfilePicture} from "../../../store/actions";

const useStyles = makeStyles(() => ({
  root: {},
  paper: {
    borderRadius: '24px!important'
  },
  mainDiv: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  fileBtn: {
    marginBottom: 20
  },
  uploadBtn: {
    fontSize: '15px!important',
    backgroundColor: '#017AEF!important',
    '& span': {
      color: 'white',
      textTransform: 'none'
    },
    '& hover': {
      backgroundColor: '#0f9fef!important',
      opacity: 0.95
    }
  }
}));

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
      paddingTop: 26
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      background: 'transparent!important'
    },
    header: {
      color: '#212D40',
      fontFamily: 'Spartan-bold',
      fontSize: 24,
      paddingLeft: 17,
      paddingBottom: 7,
      display: 'flex',
      alignItems: 'center'
    }
  });

const DialogTitle = withStyles(styles)((props: any) => {
  const {children, classes, onClose, ...other} = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <div className={classes.header}>
        {children}
        {onClose ? (
          <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
            <img src={'/assets/icons/creatordashboard/project-modal-close.svg'} alt={'img'}/>
          </IconButton>
        ) : null}
      </div>
    </MuiDialogTitle>
  );
});

interface IProps {
  open: boolean;
  onClose: () => void;
  onFailed: () => void;
}

const UploadProfilePicture = (props: IProps) => {
  const classes = useStyles();
  const { open, onClose, onFailed } = props;
  const dispatch = useDispatch();
  const apolloClient = useSelector((state: any) => state.apolloClient);
  const loginUserData = localStorage.getItem("user_data") ? JSON.parse(localStorage.getItem("user_data") || "{}") : {};
  const [upImg, setUpImg] = useState('');
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [uploadLoading, setUploadLoading] = useState(false);

  useEffect(() => {
    open && initData();
  }, [open]);

  useEffect(() => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image: any = imgRef.current;
    const canvas: any = previewCanvasRef.current;
    const crop: any = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );
  }, [completedCrop]);

  const initData = () => {
    setUpImg('');
    setCrop(undefined);
    setCompletedCrop(undefined);
    setUploadLoading(false);
  };

  const onSelectFile = (e: any) => {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined); // Makes crop preview update between images.
      const reader: any = new FileReader();
      reader.addEventListener('load', () =>
        setUpImg(reader.result.toString() || ''),
      );
      reader.readAsDataURL(e.target.files[0])
    }
  };

  const onCloseModal = () => {
    onClose();
  };

  const onImageLoad = (e: any) => {
    imgRef.current = e.currentTarget;

    const { width, height } = e.currentTarget;

    // This is to demonstate how to make and center a % aspect crop
    // which is a bit trickier so we use some helper functions.
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        1,
        width,
        height,
      ),
      width,
      height,
    );

    setCrop(crop)
  };

  const onUpload = (canvas: any, crop: any) => {
    if (!crop || !canvas) {
      return;
    }
    setUploadLoading(true);
    canvas.toBlob((blob: any) => {
      const file = new File([blob], "profile");
      const request = {
        userid: loginUserData.user_id,
        image: [file]
      };
      dispatch(updateUserProfilePicture(apolloClient.client, request, (res: any) => {
        if (res) {
          setUploadLoading(false);
          onCloseModal();
        } else {
          setUploadLoading(false);
          onFailed();
        }
      }));
    });
  };

  return (
    <Dialog
      disableEscapeKeyDown
      fullWidth={true}
      maxWidth={'sm'}
      scroll='paper'
      open={open}
      classes={{
        root: classes.root,
        paper: classes.paper
      }}
    >
      <DialogTitle onClose={onCloseModal}>
        Upload Profile Picture
      </DialogTitle>
      <Divider/>
      <DialogContent>
        <div className={classes.mainDiv}>
          <div>
            <input type="file" accept="image/*" className={classes.fileBtn} onChange={onSelectFile} />
          </div>
          {Boolean(upImg) && (
            <ReactCrop
              crop={crop}
              circularCrop={true}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
            >
              <img
                alt="Crop me"
                src={upImg}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
          <canvas
            ref={previewCanvasRef}
            // Rounding is important so the canvas width and height matches/is a multiple for sharpness.
            style={{
              width: completedCrop?.width || 0,
              height: completedCrop?.height || 0,
              display: 'none'
            }}
          />
        </div>
        <DialogActions style={{ marginTop: 20 }}>
          <Button onClick={onCloseModal} variant="contained" >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => onUpload(previewCanvasRef.current, completedCrop)}
            className={(!completedCrop?.width || !completedCrop?.height) ? '' : classes.uploadBtn}
            disabled={!completedCrop?.width || !completedCrop?.height}
          >
            {uploadLoading ? <CircularProgress color="inherit" size={20}/> : 'Upload'}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
};

export default UploadProfilePicture;